// externals
import { computed, observable, action } from 'mobx';
import { helpers, models } from '@kurtosys/ksys-app-template';
// models
import { ConditionalDisclaimer } from '../models/configuration/disclaimers/ConditionalDisclaimer';
import { ConditionalDisclaimers } from '../models/configuration/disclaimers/ConditionalDisclaimers';
import { DisclaimerDisplayType } from '../models/configuration/disclaimers/DisclaimerDisplayType';
import { FundType } from '../models/configuration/FundType';
import { Stores } from '../models/Stores';
// utils
import { getFundTypeId } from '../utils/getFundTypeId';
import { getCulturedDisclaimerFromResponse } from '../utils/disclaimers';
import { transformValue } from '../utils/transform/transform';
// api
import { Services } from '../services';
// stores
import {
    BaseStore,
    DataStore,
    ConditionalDisclaimerStore,
    DisclaimerSuperscriptsStore,
    DisclaimerMeta,
    GlobalDisclaimerStore,
    QueryStore,
    SuperscriptMeta,
    ConfigStore,
} from './';
import {
    IExecutionOptions,
    IQueryContext,
} from '@kurtosys/ksys-app-template/dist/models/query';
import { BaseDisclaimer } from '../models/configuration/disclaimers/BaseDisclaimer';

interface FundTypeGrouped<TDisclaimerStorage> {
    [fundType: string]: ModuleGrouped<TDisclaimerStorage>;
}

interface ModuleGrouped<TDisclaimerStorage> {
    [moduleId: string]: TDisclaimerStorage;
}

interface EntityGrouped<TDisclaimerStorage> {
    [entityClientCode: string]: TDisclaimerStorage;
}

interface SuperscriptScopes {
    module?: DisclaimerSuperscriptsStore;
    fundCard?: DisclaimerSuperscriptsStore;
}

export enum SuperscriptScope {
    module = 'module',
    fundCard = 'fundCard',
}

export class DisclaimerStore extends BaseStore {
    static maxOrder = 999999;

    /**
     * Contains all disclaimer types linked to entities
     *
     * @private
     */
    @observable.ref
    private entityDisclaimersByType: EntityGrouped<Map<string, string>> = {};

    /**
     * The superscript scopes hold the superscript numbers linked to the disclaimer types,
     * which are defined in each scope by the app configuration.
     *
     * These scopes are thus grouped by the fund type id & module id.
     *
     * @private
     */
    @observable.ref
    private superscriptScopes: FundTypeGrouped<SuperscriptScopes> = {};

    @computed
    get config(): ConfigStore {
        return this.dataStore && this.dataStore.config;
    }

    @computed
    get culture(): string {
        return this.dataStore && this.dataStore.culture;
    }

    @computed
    get dataStore(): DataStore {
        return this.stores && this.stores.data;
    }

    @computed
    get queryStore(): QueryStore {
        return this.stores.query;
    }

    @computed
    get services(): Services {
        return this.dataStore && this.dataStore.services;
    }

    @computed
    get targetFundTypeId() {
        if (this.config) {
            return getFundTypeId(this.config.selectedFundType as FundType);
        }
    }

    @computed
    get targetModuleId() {
        if (this.config) {
            return this.config.selectedModule.id;
        }
    }

    constructor(sharedStores: Stores) {
        super(sharedStores);
    }

    initialize() {
        ConditionalDisclaimerStore.initialize(this.stores);
    }

    /**
     * Create a list of superscript references for defined disclaimer meta
     *
     * The scope applies to everything from Fund Type Tabs and down,
     * with the exception of the Fund Card contents
     *
     * @param moduleDisclaimerMeta
     */
    @action
    loadModuleSuperscripts(moduleDisclaimerMeta: Array<DisclaimerMeta>) {
        const module = new DisclaimerSuperscriptsStore(
            moduleDisclaimerMeta,
            SuperscriptScope.module,
            this.config.moduleFootnoteOptions
        );
        const list = [];
        module.forEach((superscript, type) =>
            list.push({
                type,
                superscript,
            })
        );
        this.setSuperscriptDisclaimers({
            module,
        });
    }

    /**
     * This should be called when ever the entity data changes, which in turn resets the superscript
     * references for the given tab state.
     *
     */
    @action
    loadModuleDisclaimers() {
        if (this.dataStore) {
            const moduleDisclaimerMeta: Array<DisclaimerMeta> = [];
            const conditionalDisclaimers = ConditionalDisclaimerStore.getConfigForModule();
            const entityDisclaimerTypes: EntityGrouped<Map<
                string,
                string
            >> = {};

            const { funds } = this.dataStore;
            funds.forEach(entity => {
                this.loadDisclaimersForEntity(
                    entity,
                    moduleDisclaimerMeta,
                    conditionalDisclaimers,
                    entityDisclaimerTypes
                );
            });
            // Update observable refs
            this.entityDisclaimersByType = entityDisclaimerTypes;
            this.loadModuleSuperscripts(moduleDisclaimerMeta);
        }
    }

    /**
     * Create a list of superscript reference for defined disclaimer meta
     *
     * The scope applies only to the Fund Card contents
     *
     */
    @action
    loadFundCardSuperscripts(fundCardDisclaimerMeta: Array<DisclaimerMeta>) {
        const fundCard = new DisclaimerSuperscriptsStore(
            fundCardDisclaimerMeta,
            SuperscriptScope.fundCard,
            this.config.rowConfigFootnoteOptions,
            this.config.rowConfigFootnoteScope === 'shared' &&
                this.superscriptDisclaimers.module
        );
        const list = [];
        fundCard.forEach((superscript, type) =>
            list.push({
                type,
                superscript,
            })
        );
        this.setSuperscriptDisclaimers({
            fundCard,
        });
    }

    /**
     *
     *
     */
    @action
    loadFundCardDisclaimers(entity: models.api.search.IEntity) {
        const fundCardDisclaimerMeta: Array<DisclaimerMeta> = [];
        const conditionalDisclaimers = ConditionalDisclaimerStore.getConfigForFundCard();

        /**
         * create a working copy of the disclaimer types & texts by type based on existing ones
         * (saves memory and processing as many would have been fetched in this.loadDisclaimers)
         */
        const entityDisclaimerTypes: EntityGrouped<Map<string, string>> = this
            .entityDisclaimersByType;
        this.loadDisclaimersForEntity(
            entity,
            fundCardDisclaimerMeta,
            conditionalDisclaimers,
            entityDisclaimerTypes
        );

        this.entityDisclaimersByType = entityDisclaimerTypes;

        this.loadFundCardSuperscripts(fundCardDisclaimerMeta);
    }

    @action
    loadDisclaimersForEntity(
        entity: models.api.search.IEntity & { _disclaimersLoaded?: boolean },
        disclaimerMeta: Array<DisclaimerMeta>,
        conditionalDisclaimers: ConditionalDisclaimers,
        entityDisclaimersByTypes: EntityGrouped<Map<string, string>>
    ) {
        // Load Disclaimers from entity API response into Maps for quick access;
        const { disclaimers: entityDisclaimers = {} } = entity;
        const disclaimerTypesFromEntity = Object.keys(entityDisclaimers);
        disclaimerTypesFromEntity.forEach(entityDisclaimerType => {
            // create entity disclaimer type set if undefined
            if (!entityDisclaimersByTypes[entity.clientCode]) {
                entityDisclaimersByTypes[entity.clientCode] = new Map();
            }

            // don't add disclaimers that already exist
            if (
                !entityDisclaimersByTypes[entity.clientCode].has(
                    entityDisclaimerType
                )
            ) {
                // get disclaimer text based on current page culture
                const culturedDisclaimer = getCulturedDisclaimerFromResponse(
                    this.stores,
                    entityDisclaimers[entityDisclaimerType]
                );

                // store text on disclaimer store for entity
                if (culturedDisclaimer) {
                    entityDisclaimersByTypes[entity.clientCode].set(
                        entityDisclaimerType,
                        culturedDisclaimer
                    );
                }
            }
        });

        // Build up module superscripts based on disclaimer configuration
        if (conditionalDisclaimers.length > 0) {
            const { entityType } = this.dataStore.config;
            const queryContext: IQueryContext = {
                entityByType: { [entityType || 'CLSS']: entity },
            };

            const executionOptions: IExecutionOptions = this.queryStore.getExecutionOptionsUsingContext(
                queryContext
            );
            conditionalDisclaimers.forEach(conditionalDisclaimer => {
                const disclaimerType = conditionalDisclaimer.disclaimerType;

                /*
                    If configuration has specified a hard link to the entity or global disclaimers, 
                    then this should be respected, otherwise allow entity to global fallback.
                */
                let disclaimerText;
                if (conditionalDisclaimer.linked !== undefined) {
                    if (conditionalDisclaimer.linked === true) {
                        disclaimerText = entityDisclaimersByTypes[
                            entity.clientCode
                        ]?.get(disclaimerType);
                    } else {
                        disclaimerText = GlobalDisclaimerStore.get(
                            disclaimerType
                        );
                    }
                } else {
                    const entityDisclaimerText = entityDisclaimersByTypes[
                        entity.clientCode
                    ]?.get(disclaimerType);
                    const globalDisclaimerText = GlobalDisclaimerStore.get(
                        disclaimerType
                    );
                    disclaimerText =
                        entityDisclaimerText || globalDisclaimerText;
                }

                if (
                    conditionalDisclaimer.display ===
                        DisclaimerDisplayType.superscript &&
                    this.isDisclaimerConditionalValid(
                        entity,
                        conditionalDisclaimer,
                        this.createExecutionOptionsForEntity(entity)
                    ) &&
                    !!disclaimerText
                ) {
                    if (conditionalDisclaimer.transforms) {
                        disclaimerText = transformValue(
                            disclaimerText,
                            conditionalDisclaimer.transforms,
                            this.stores.translation,
                            executionOptions
                        );
                    }

                    /* 
                        disclaimerMeta used to populate the DisclaimerSuperscriptsStore.

                        We do not check for duplicate disclaimer types here, 
                        due to the order field that could make a type a higher priority.

                        The DisclaimerSuperscriptsStore will reorder and skip duplicates at that point.
                    */
                    disclaimerMeta.push({
                        type: conditionalDisclaimer.disclaimerType,
                        text: disclaimerText,
                        order: conditionalDisclaimer.order,
                    });
                }
            });
        }

        entity._disclaimersLoaded = true;
    }

    /**
     * Returns a list of global disclaimer texts based on provided disclaimerTypes.
     *
     * Additionally it returns superscript objects related to the scope defined.
     *
     * This is generally used to output disclaimers at the bottom of the page (module) or the bottom of a fund card.
     * ~ (for superscript output, the scope is required)
     *
     * @param [disclaimerConfigs=[]]
     * @param [includeSuperscriptedDisclaimers=false]
     * @param [superscriptScope]
     * @returns string[] | { character: string; content: string }[]
     */
    getDisclaimersForDisplay(
        disclaimerConfigs: Array<BaseDisclaimer> = [],
        includeSuperscriptedDisclaimers: boolean = false,
        superscriptScope?: SuperscriptScope,
        queryContext?: IQueryContext
    ): Array<string | SuperscriptMeta> {
        const disclaimers: Array<any> = [];

        const executionOptions: IExecutionOptions = this.queryStore.getExecutionOptionsUsingContext(
            queryContext
        );
        // Add targeted global disclaimers (if not already linked with a superscript)
        disclaimerConfigs.forEach(disclaimer => {
            const { disclaimerType } = disclaimer;
            if (GlobalDisclaimerStore.has(disclaimerType)) {
                let globalDisclaimerText = GlobalDisclaimerStore.get(
                    disclaimerType
                );
                if (
                    !this.superscriptDisclaimers[superscriptScope] ||
                    !this.superscriptDisclaimers[superscriptScope].has(
                        disclaimerType,
                        globalDisclaimerText
                    )
                ) {
                    if (disclaimer.transforms && queryContext) {
                        globalDisclaimerText = transformValue(
                            globalDisclaimerText,
                            disclaimer.transforms,
                            this.stores.translation,
                            executionOptions
                        );
                    }
                    disclaimers.push(globalDisclaimerText);
                }
            }
        });

        // Add all superscripts
        if (
            includeSuperscriptedDisclaimers &&
            superscriptScope &&
            this.superscriptDisclaimers[superscriptScope]
        ) {
            this.superscriptDisclaimers[superscriptScope].forEach(meta => {
                disclaimers.push(meta);
            });
        }

        return disclaimers;
    }

    getSuperscripts(
        entity: any,
        conditionalDisclaimers: ConditionalDisclaimers,
        scope: SuperscriptScope
    ): Array<SuperscriptMeta> {
        return (this.getDisclaimers(
            entity,
            conditionalDisclaimers,
            DisclaimerDisplayType.superscript,
            scope
        ) as Array<SuperscriptMeta>).sort((a, b) => a.order - b.order);
    }

    getTooltipDisclaimers(
        entity: any,
        conditionalDisclaimers: ConditionalDisclaimers
    ): Array<DisclaimerMeta> {
        return this.getDisclaimers(
            entity,
            conditionalDisclaimers,
            DisclaimerDisplayType.tooltip
        ) as Array<DisclaimerMeta>;
    }

    /**
     * Get the superscript scopes (Module &/ FundCard) based on the current Fund Type & Module selection
     *
     * @readonly
     */
    @computed
    private get superscriptDisclaimers(): SuperscriptScopes {
        const scopes =
            (this.superscriptScopes &&
                this.superscriptScopes[this.targetFundTypeId] &&
                this.superscriptScopes[this.targetFundTypeId][
                    this.targetModuleId
                ]) ||
            {};
        return scopes;
    }

    /**
     * Set the superscript scopes (Module &/ FundCard) based on the current Fund Type & Module selection
     */
    @action
    private setSuperscriptDisclaimers(superscriptScopes: SuperscriptScopes) {
        const workingSuperscriptsScopes = this.superscriptScopes;
        if (!workingSuperscriptsScopes[this.targetFundTypeId]) {
            workingSuperscriptsScopes[this.targetFundTypeId] = {};
        }

        if (
            !workingSuperscriptsScopes[this.targetFundTypeId][
                this.targetModuleId
            ]
        ) {
            workingSuperscriptsScopes[this.targetFundTypeId][
                this.targetModuleId
            ] = {};
        }

        const currentScopes =
            workingSuperscriptsScopes[this.targetFundTypeId][
                this.targetModuleId
            ];
        workingSuperscriptsScopes[this.targetFundTypeId][
            this.targetModuleId
        ] = {
            ...currentScopes,
            ...superscriptScopes,
        };

        this.superscriptScopes = { ...workingSuperscriptsScopes };
    }

    private getDisclaimerTextForType(
        entity: any,
        conditionalDisclaimer: ConditionalDisclaimer
    ) {
        if (
            this.isDisclaimerConditionalValid(
                entity,
                conditionalDisclaimer,
                this.createExecutionOptionsForEntity(entity)
            )
        ) {
            const { disclaimerType } = conditionalDisclaimer;
            const entityDisclaimerTypes =
                this.entityDisclaimersByType &&
                this.entityDisclaimersByType[entity?.clientCode];
            if (
                (entityDisclaimerTypes &&
                    entityDisclaimerTypes.has(disclaimerType)) ||
                GlobalDisclaimerStore.has(disclaimerType)
            ) {
                const entityDisclaimerText = this.getDisclaimerText(
                    entity,
                    conditionalDisclaimer
                );
                return {
                    type: disclaimerType,
                    text: entityDisclaimerText,
                } as DisclaimerMeta;
            }
        }
        return undefined;
    }

    private getDisclaimerSuperscriptForType(
        entity: any,
        conditionalDisclaimer: ConditionalDisclaimer,
        scope: SuperscriptScope
    ) {
        const { disclaimerType } = conditionalDisclaimer;
        if (
            !this.isDisclaimerConditionalValid(
                entity,
                conditionalDisclaimer,
                this.createExecutionOptionsForEntity(entity)
            )
        ) {
            return undefined;
        }
        const entityDisclaimerText = this.getDisclaimerText(
            entity,
            conditionalDisclaimer
        );
        const superscriptMeta =
            this.superscriptDisclaimers[scope] &&
            this.superscriptDisclaimers[scope].get(
                disclaimerType,
                entityDisclaimerText
            );
        return superscriptMeta;
    }

    private createExecutionOptionsForEntity(entity: any) {
        if (entity) {
            return this.queryStore.getExecutionOptionsUsingContext({
                entityByType: {
                    [entity.type]: entity,
                },
            });
        }
        return this.queryStore.executionOptions;
    }

    /**
     * Validates if the disclaimer meets its conditional requirements.
     *
     * @private
     * @param instanceContext For Fund Center this just tends to be the entity
     * @param conditionalDisclaimer
     * @param [existingConditionalHelper]
     * @returns boolean
     */
    private isDisclaimerConditionalValid(
        instanceContext: any,
        conditionalDisclaimer: ConditionalDisclaimer,
        executionOptions: models.query.IExecutionOptions = this.queryStore
            .executionOptions
    ) {
        const { conditional } = conditionalDisclaimer;
        if (conditional) {
            const conditionalHelper = new helpers.ConditionalHelper(
                conditional
            );
            if (
                !conditionalHelper.matches(
                    instanceContext,
                    undefined,
                    executionOptions
                )
            ) {
                return false;
            }
        }
        return true;
    }

    private getDisclaimerText(
        entity: models.api.search.IEntity,
        disclaimer: BaseDisclaimer
    ) {
        let response;
        const { disclaimerType, linked } = disclaimer;
        const hasEntityDisclaimerText =
            entity &&
            entity.clientCode &&
            this.entityDisclaimersByType &&
            this.entityDisclaimersByType[entity.clientCode] &&
            this.entityDisclaimersByType[entity.clientCode].has(disclaimerType);

        const hasGlobalDisclaimerText = GlobalDisclaimerStore.has(
            disclaimerType
        );

        if (linked === true && hasEntityDisclaimerText) {
            response = this.entityDisclaimersByType[entity.clientCode].get(
                disclaimerType
            );
        } else if (linked === false && hasGlobalDisclaimerText) {
            response = GlobalDisclaimerStore.get(disclaimerType);
        } else if (linked === undefined) {
            if (hasEntityDisclaimerText) {
                response = this.entityDisclaimersByType[entity.clientCode].get(
                    disclaimerType
                );
            } else if (hasGlobalDisclaimerText) {
                response = GlobalDisclaimerStore.get(disclaimerType);
            }
        }

        const { entityType } = this.dataStore.config;
        const queryContext: IQueryContext = {
            entityByType: { [entityType || 'CLSS']: entity },
        };

        const executionOptions: IExecutionOptions = this.queryStore.getExecutionOptionsUsingContext(
            queryContext
        );
        if (disclaimer.transforms) {
            response = transformValue(
                response,
                disclaimer.transforms,
                this.stores.translation,
                executionOptions
            );
        }
        return response;
    }

    private getConditionalDisclaimerDisplayType(
        conditionalDisclaimer: ConditionalDisclaimer
    ) {
        if (this.dataStore.isMobile && conditionalDisclaimer.mobileDisplay) {
            return conditionalDisclaimer.mobileDisplay;
        }
        return (
            conditionalDisclaimer.display || DisclaimerDisplayType.superscript
        );
    }

    private getDisclaimer(
        entity: any,
        conditionalDisclaimer: ConditionalDisclaimer,
        targetDisclaimerDisplayType: DisclaimerDisplayType,
        scope?: SuperscriptScope
    ): SuperscriptMeta | DisclaimerMeta | undefined {
        if (
            targetDisclaimerDisplayType === DisclaimerDisplayType.superscript &&
            scope
        ) {
            return this.getDisclaimerSuperscriptForType(
                entity,
                conditionalDisclaimer,
                scope
            );
        } else if (
            targetDisclaimerDisplayType !== DisclaimerDisplayType.superscript
        ) {
            return this.getDisclaimerTextForType(entity, conditionalDisclaimer);
        }
    }

    private getDisclaimers(
        entity: any,
        conditionalDisclaimers: ConditionalDisclaimers,
        targetDisclaimerDisplayType: DisclaimerDisplayType,
        scope?: SuperscriptScope
    ): Array<DisclaimerMeta | SuperscriptMeta> {
        const uniqueIds = new Set();
        return conditionalDisclaimers
            .filter(conditionalDisclaimer => {
                return (
                    this.getConditionalDisclaimerDisplayType(
                        conditionalDisclaimer
                    ) === targetDisclaimerDisplayType
                );
            })
            .reduce((list, conditionalDisclaimer) => {
                const result = this.getDisclaimer(
                    entity,
                    conditionalDisclaimer,
                    targetDisclaimerDisplayType,
                    scope
                );
                if (!!result) {
                    const validationId =
                        (result as SuperscriptMeta).superscript || result.text;
                    if (!uniqueIds.has(validationId)) {
                        uniqueIds.add(validationId);
                        list.push(result);
                    }
                }
                return list;
            }, []);
    }
}



// WEBPACK FOOTER //
// ./src/stores/DisclaimerStore.tsx